import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import i18next from 'i18next';
import { logEvent } from '../lib/telemetry';

interface ErrorBoundaryProps {
  children: ReactNode;
  currentPage?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
    console.error('ErrorBoundary caught:', error, errorInfo);
    // Feed the breadcrumb pipeline: the server turns client_error_boundary
    // events into auto-filed bug reports, so a render crash is no longer
    // invisible just because the user can't tap "report a problem".
    void logEvent('client_error_boundary', error.message || error.name || 'render error', {
      severity: 'error',
      metadata: {
        stack: error.stack ?? null,
        component_stack: errorInfo.componentStack ?? null,
        page: this.props.currentPage ?? null,
      },
    });
  }

  handleReload = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      const { error } = this.state;
      const t = (key: string) => i18next.t(key, { ns: 'common' });

      return (
        <div className="min-h-screen bg-subtle flex items-center justify-center p-6">
          <div className="max-w-md w-full">
            <div className="bg-surface rounded-xl shadow-lg border border-line overflow-hidden">
              <div className="bg-red-50 border-b border-red-100 dark:bg-red-500/10 dark:border-red-500/30 px-6 py-5 flex items-center gap-4">
                <div className="w-12 h-12 bg-red-100 dark:bg-red-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-red-900 dark:text-red-200">{t('feedback.errorBoundaryTitle')}</h2>
                  <p className="text-sm text-red-800 dark:text-red-300 mt-0.5">{t('feedback.errorBoundaryMessage')}</p>
                </div>
              </div>

              <div className="px-6 py-5 space-y-4">
                <div className="bg-subtle rounded-lg p-3 border border-line">
                  <p className="text-xs font-mono text-theme-secondary break-all line-clamp-3">
                    {error?.message || t('feedback.errorBoundaryMessage')}
                  </p>
                </div>

                <button
                  onClick={this.handleReload}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
                >
                  <RefreshCw size={16} />
                  {t('feedback.tryAgain')}
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
